import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead'
import moment from 'moment'
import API from '../../../../services/API'
import { ControlRoomContext } from './ControlPage'

const POLL_MS = 5000

// Dispatcher side of the resource ↔ control room chat. The chat belongs to
// the RESOURCE — every crew member on it and everyone in the control room
// sees the same thread. Messages show who physically sent them.
const ResourceChat = ({
  resourceID,
  resourceCallSign,
  onMessagesViewed,
  height = 260,
}) => {
  // Sending is a dispatcher action (server enforces it too) — viewers can
  // read the thread but the composer is hidden.
  const { canDispatch = true } = useContext(ControlRoomContext) ?? {}
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const scrollRef = useRef(null)
  const lastCountRef = useRef(0)

  const load = () => {
    if (!resourceID) return
    API.get(`/commandcontrol/resources/${resourceID}/chat`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setMessages(res.data)
          setFetchError(false)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    setMessages([])
    setLoading(true)
    load()
    const interval = setInterval(load, POLL_MS)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceID])

  useEffect(() => {
    if (messages.length !== lastCountRef.current) {
      lastCountRef.current = messages.length
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [messages])

  // Viewing alone does NOT clear unread — crew messages stay flagged until a
  // dispatcher replies (server clears on reply) or explicitly marks them read.
  const hasUnreadFromCrew = messages.some(
    (message) => !message.fromControl && !message.readByControl,
  )

  const markRead = () => {
    API.post(`/commandcontrol/resources/${resourceID}/chat/mark-read`)
      .then(() => {
        onMessagesViewed?.()
        load()
      })
      .catch(() => {})
  }

  const handleSend = (e) => {
    e?.preventDefault()
    const message = draft.trim()
    if (!message || sending) return
    setSending(true)
    API.post(`/commandcontrol/resources/${resourceID}/chat`, { message })
      .then((res) => {
        if (res.status === 200) {
          setDraft('')
          load()
          // The reply marked the crew's messages read server-side
          onMessagesViewed?.()
        }
      })
      .finally(() => setSending(false))
  }

  return (
    <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
      <Box
        ref={scrollRef}
        sx={{
          height,
          flex: height === 'flex' ? 1 : undefined,
          minHeight: 120,
          overflowY: 'auto',
          borderRadius: 2,
          border: '1px solid #E2E8F0',
          backgroundColor: '#F8FAFC',
          p: 1.25,
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size="1.2rem" />
          </Box>
        ) : fetchError ? (
          <Alert severity="warning">Unable to load messages.</Alert>
        ) : messages.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            py={2}
          >
            No messages with this resource yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {messages.map((message) => {
              const senderLabel = message.fromControl
                ? `Control${message.authorName ? ` · ${message.authorName}` : ''}`
                : `${resourceCallSign || 'Crew'}${message.authorName ? ` · ${message.authorName}` : ''}`
              return (
                <Stack
                  key={message._id}
                  direction={message.fromControl ? 'row-reverse' : 'row'}
                  spacing={0.75}
                  alignItems="flex-end"
                >
                  <Tooltip title={message.authorName || ''}>
                    <Avatar
                      src={message.author?.photograph}
                      sx={{ width: 26, height: 26, fontSize: 13 }}
                    >
                      {(message.authorName || '?')[0]}
                    </Avatar>
                  </Tooltip>
                  <Box
                    sx={{
                      maxWidth: '78%',
                      backgroundColor: message.fromControl
                        ? '#1E7F88'
                        : '#FFFFFF',
                      color: message.fromControl ? '#FFFFFF' : '#111827',
                      border: message.fromControl
                        ? 'none'
                        : '1px solid #E2E8F0',
                      borderRadius: 2.5,
                      px: 1.25,
                      py: 0.75,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: message.fromControl ? '#FFFFFF' : '#111827',
                      }}
                    >
                      {senderLabel}
                      {' · '}
                      {moment(message.createdAt).format('HH:mm')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        color: message.fromControl ? '#FFFFFF' : '#111827',
                      }}
                    >
                      {message.message}
                    </Typography>
                  </Box>
                </Stack>
              )
            })}
          </Stack>
        )}
      </Box>

      {hasUnreadFromCrew && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: 2,
            backgroundColor: 'rgba(227,37,37,0.08)',
            border: '1px solid rgba(227,37,37,0.25)',
          }}
        >
          <MarkChatReadIcon sx={{ fontSize: 16, color: '#E32525' }} />
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 700 }}>
            Unread crew messages — reply or mark as read to clear
          </Typography>
          <Button size="small" onClick={markRead} sx={{ fontWeight: 800 }}>
            Mark as read
          </Button>
        </Stack>
      )}

      {canDispatch && (
        <Stack
          component="form"
          onSubmit={handleSend}
          direction="row"
          spacing={1}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={`Message ${resourceCallSign || 'the crew'}…`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!draft.trim() || sending}
            endIcon={<SendIcon />}
            sx={{ borderRadius: 2, fontWeight: 800, whiteSpace: 'nowrap' }}
          >
            Send
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export default ResourceChat
